import { useActionData, useNavigation, useParams, useSearchParams } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { Entry, SpaceEvent } from "@repo/shared-types";
import { partLabel } from "@/lib/event-form";
import { useSpaceContext, loadSpaceContext, type SpaceContext } from "@/lib/events";
import { readOverallBoard, readPartBoard, useOverallBoard, usePartBoard } from "@/lib/event-boards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { hasOverall, isRanked } from "@/lib/results";
import { tenantPath } from "@/lib/tenants";
import { ErrorNote } from "@/components/form";
import { EventBoard, OverallRule, type BoardData } from "@/components/leaderboard";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import NotFound from "@/routes/not-found";

/**
 * One leaderboard on a screen of its own (decided with Tiago 10 October): a
 * part's, at `…/events/:event/parts/:part`, or the event's overall ranking,
 * at `…/events/:event/overall`. The event's page lists the parts and links
 * here; this screen has the level and division tabs, the pages, and — for
 * whoever holds the grants — verifying and hiding from a line, posted to the
 * event page's own action, which takes the same intents.
 *
 * A part without a leaderboard, an event without an overall, or a part that
 * is not the event's, is the 404 a missing event is.
 */

function notFound(): never {
  throw new Response("Not Found", { status: 404 });
}

export async function boardLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const [context, event, entry] = await Promise.all([
    loadSpaceContext(request, slug),
    read(queries.event(slug, eventId)),
    read(queries.myEntry(slug, eventId)),
  ]);
  const search = new URL(request.url).searchParams;
  const division = context.profile.division;

  if (!params.part) {
    if (!hasOverall(event)) notFound();
    await readOverallBoard(slug, event, entry, division, search);
    return null;
  }
  const part = event.parts.find((candidate) => candidate.id === params.part);
  if (!part || !isRanked(part)) notFound();
  await readPartBoard(slug, event.id, part, entry, division, search);
  return null;
}

export default function BoardPage() {
  const params = useParams();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const context = useSpaceContext(slug);
  const { data: event } = useSuspenseQuery(queries.event(slug, eventId));
  const { data: entry } = useSuspenseQuery(queries.myEntry(slug, eventId));
  if (!params.part) return <OverallBoard slug={slug} context={context} event={event} entry={entry} />;
  const part = event.parts.find((candidate) => candidate.id === params.part);
  // Gone since the loader looked — a fresh read of the event dropped it: the
  // same 404, drawn here, since only a loader's thrown Response reads as one.
  if (!part || !isRanked(part)) return <NotFound />;
  return <PartBoard slug={slug} context={context} event={event} entry={entry} part={part} />;
}

type BoardProps = { slug: string; context: SpaceContext; event: SpaceEvent; entry: Entry | null };

function PartBoard({ part, ...props }: BoardProps & { part: SpaceEvent["parts"][number] }) {
  const [search] = useSearchParams();
  const { slug, context, event, entry } = props;
  const board = usePartBoard(slug, event.id, part, entry, context.profile.division, search);
  // None only for a board never read, which the loader's warming leaves none of.
  return board && <BoardScreen {...props} board={board} />;
}

function OverallBoard(props: BoardProps) {
  const [search] = useSearchParams();
  const { slug, context, event, entry } = props;
  const board = useOverallBoard(slug, event, entry, context.profile.division, search);
  // None only for a board never read, which the loader's warming leaves none of.
  return board && <BoardScreen {...props} board={board} />;
}

function BoardScreen({ context, event, board }: BoardProps & { board: BoardData }) {
  const { tenant, canModerate, canVerify } = context;
  const result = useActionData() as { error?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const eventPath = `${tenantPath(tenant)}/events/${event.id}`;
  const index = event.parts.findIndex((part) => part.id === board.choice.partId);
  const part = index >= 0 ? event.parts[index]! : null;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={eventPath}>{event.title}</BackLink>
        <p className="text-sm font-medium text-muted-foreground">{part ? `Part ${partLabel(index)}` : "Overall"}</p>
        <PageTitle of={[part ? part.title : "Overall ranking", event.title]} />
        <h1 className="page-title">{part ? part.title : "Overall ranking"}</h1>
        {part && <p className="text-muted-foreground">{part.summary}</p>}
        {part?.description && <p className="whitespace-pre-line">{part.description}</p>}
        {!part && <OverallRule event={event} />}
      </div>

      {/* A refusal stays here, at the top; a verification or a hiding that
          worked says so in a toast, from the action. */}
      {result?.error && <ErrorNote message={result.error} />}

      <EventBoard
        event={event}
        eventPath={eventPath}
        data={board}
        label={part ? `Part ${partLabel(index)} leaderboard` : "Overall ranking"}
        canModerate={canModerate}
        canVerify={canVerify}
        busy={busy}
      />
    </section>
  );
}
